import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Grid } from "@mui/material";

import UnderlineButton from "../../../../../../components/Custom/UnderlineButton";
import CustomHelper from "../../../../../../components/Helper/CustomHelper";
import SessionPositionEditableSheetOptimized from "../../../event-details/SessionPositionEditableSheetOptimized";
import CalendarDatesPanel from "./CalendarDatesPanel";
import { EachDateOfSessionCheckbox, MealOptionsField } from "./PositionFormFields";
import ShiftRowsList from "./ShiftRowsList";
import type { PositionFormik, SessionDates } from "./positionForm.types";
import { buildManageSheetRows } from "./positionForm.utils";
import useShiftScheduleSync from "./useShiftScheduleSync";

type AddShiftFieldsProps = {
  formik: PositionFormik;
  session: SessionDates;
  afterAddPositions?: () => void;
};

/**
 * Add flow: pick dates (calendar or list view), one row per date, then the
 * "Manage positions in depth" sheet for fine-tuning before saving.
 */
const AddShiftFields = ({ formik, session, afterAddPositions }: AddShiftFieldsProps) => {
  const { values, setValues, handleBlur } = formik;
  const eventInfo = useSelector(
    (state: { createContactInfo?: { eventInfo?: Record<string, unknown> } }) =>
      state.createContactInfo?.eventInfo,
  );
  const [isSheetOpen, setIsSheetOpen] = useState(false);

  useShiftScheduleSync(formik, session);

  const isCalendarView = values?.currentView === "calendar";
  const hasRows = values?.positions?.length > 0 && values?.positionTimeInfo?.length > 0;
  // `session` is rebuilt by the parent on every render: depend on its dates only,
  // otherwise the open sheet receives new data and drops the user's unsaved edits.
  const sheetRows = useMemo(
    () => buildManageSheetRows(values, session),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [values, session?.sessionStart, session?.sessionEnd],
  );

  return (
    <>
      {values?.currentView === "normal" ? (
        <Grid item sx={{ width: "100%", marginBottom: "0.85rem" }}>
          <EachDateOfSessionCheckbox values={values} setValues={setValues} />
        </Grid>
      ) : null}

      {isCalendarView ? (
        <CalendarDatesPanel
          values={values}
          setValues={setValues}
          handleBlur={handleBlur}
          session={session}
        />
      ) : null}

      <ShiftRowsList formik={formik} session={session} />

      {hasRows ? (
        <Grid item sx={{ textAlign: "right", width: "100%" }}>
          <CustomHelper
            isText
            message="View a spreadsheet-style breakdown of all your selected dates and positions"
          >
            <UnderlineButton
              title="Manage positions in depth"
              onClick={() => setIsSheetOpen(true)}
              styles={{ fontSize: "17px" }}
            />
          </CustomHelper>
        </Grid>
      ) : null}

      <Grid
        item
        sx={{
          width: "50%",
          ...(isCalendarView ? { paddingTop: "0.5rem !important" } : {}),
          "@media (max-width: 600px)": { width: "100%" },
        }}
      >
        <MealOptionsField {...formik} />
      </Grid>

      {isSheetOpen ? (
        <SessionPositionEditableSheetOptimized
          eventInfo={eventInfo}
          data={sheetRows}
          session={session}
          sessions={[session]}
          open={isSheetOpen}
          successCallback={() => afterAddPositions?.()}
          handleClose={() => setIsSheetOpen(false)}
          hideSessions
          isManagePositions
          allowCopyPositions
        />
      ) : null}
    </>
  );
};

export default AddShiftFields;
